import React from 'react';
import { resolveReportCount } from './reportCount';

export interface CalendarReportsBadgeProps {
  /** Active deliverable count for the deal; null/undefined/NaN/negative render as empty. */
  count: number | null | undefined;
  /** True while the reports popover is open — flips the palette navy → amber. */
  active?: boolean;
}

/**
 * Smart Calendar™ "Report(s)" cell badge: a 28px circular count for a deal with at
 * least one active deliverable, or a quiet em-dash when it has none. Click handling
 * lives with the antd `Popover` that wraps this as its trigger child — the button
 * deliberately declares no `onClick` of its own.
 *
 * The raw count is normalised through the shared `resolveReportCount` helper — the same
 * one `CalendarReportsCell` uses to decide whether to mount a `Popover` at all — so the
 * badge and the popover gate can never disagree about what counts as "no reports".
 * @param {CalendarReportsBadgeProps} props - Badge inputs.
 * @returns {React.ReactElement} The count circle or an em-dash.
 */
export function CalendarReportsBadge({ count, active = false }: CalendarReportsBadgeProps): React.ReactElement {
  const n = resolveReportCount(count);

  if (n <= 0) {
    return <span className="text-[16px] text-[#B5BCC6]">—</span>;
  }

  const palette = active ? 'bg-[#F9A825] text-[#3A2600]' : 'bg-[#152D51] text-white';
  return (
    <button
      type="button"
      title={`${n} ${n === 1 ? 'report' : 'reports'} — click to view`}
      className={`flex items-center justify-center w-7 h-7 rounded-full border-none cursor-pointer text-[13px] font-bold leading-none transition-colors duration-150 ${palette}`}
    >
      {n}
    </button>
  );
}
